/**
 * The folder dialogs (FR-38, D16): **create**, **rename**, **move** with a
 * target picker, and **archive** — each one request, each refusal said in the
 * words a person can act on:
 *
 * - `name_taken` — sibling names are unique whatever their case;
 * - `too_deep` — nine levels below the workspace (§9-16);
 * - `cycle` — a folder cannot go under itself or one of its own folders;
 * - `cross_workspace` — a folder moves only within its workspace (documents
 *   move across, FR-16);
 * - `not_empty` — archiving is refused while it holds live documents or
 *   folders, as a workspace's is (FR-12): nothing is ever archived *with* it;
 * - `APP_OWNED` — a sibling app keeps this folder's structure (§9-19).
 *
 * The move picker offers only what the service would accept: never the folder
 * itself or anything below it, never a place that would nest its deepest
 * folder past the ninth level, and only its own workspace.
 */
import { Alert, Button, ConfirmDialog, Dialog, Input, Select } from '@fiveox/ui'
import { useEffect, useMemo, useState } from 'react'

import {
  ApiError,
  MAX_FOLDER_DEPTH,
  MAX_FOLDER_NAME,
  isAppOwned,
  type Folder,
  type ProDocClient,
} from './prodoc-client'
import { subtreeOf, useFolderList, type FolderAction } from './folder-tree'
import { errorText } from './hooks'

/** The words for a folder refusal (plan §5.1's reasons, and `APP_OWNED`). */
export function folderRefusalText(e: unknown, folder?: Pick<Folder, 'name' | 'owner_app'> | null): string {
  if (isAppOwned(e)) {
    const app = folder?.owner_app ?? (e instanceof ApiError && typeof e.details.owner_app === 'string' ? e.details.owner_app : 'its app')
    return `${folder ? `“${folder.name}”` : 'This folder'} is kept by ${app}. You can file documents into it here; renaming, moving or archiving it happens in ${app}.`
  }
  if (e instanceof ApiError) {
    switch (e.reason) {
      case 'name_taken':
        return 'A folder with that name is already here — names are unique whatever their case. Choose another.'
      case 'too_deep':
        return `Folders nest at most ${MAX_FOLDER_DEPTH} levels below the workspace. Choose a place higher up.`
      case 'cycle':
        return 'A folder cannot move under itself or one of its own folders.'
      case 'cross_workspace':
        return 'A folder moves only within its own workspace. Move its documents to another workspace instead.'
      case 'not_empty':
        return `${folder ? `“${folder.name}”` : 'This folder'} still holds documents or folders. Move them out first — archiving a folder never takes anything with it.`
      case 'archived':
        return 'That folder has been archived. Reload to see the folders as they are now.'
    }
  }
  return errorText(e)
}

/** What a dialog did, said once in a toast — wherever its tree is (the register's panel, a host's menu). */
export function folderDoneText(folder: Pick<Folder, 'name'>, kind: FolderAction['kind']): string {
  switch (kind) {
    case 'create':
      return `Folder “${folder.name}” made.`
    case 'rename':
      return `Renamed to “${folder.name}”.`
    case 'move':
      return `Moved “${folder.name}”.`
    case 'archive':
      return `Archived “${folder.name}”.`
    case 'share':
      return `Shared “${folder.name}”.`
  }
}

/** A folder's name as typed: trimmed, required, within the ceiling, no slash (a path separator in a dropped folder). */
export function folderNameError(name: string): string | undefined {
  const trimmed = name.trim()
  if (!trimmed) return 'A folder needs a name.'
  if (trimmed.length > MAX_FOLDER_NAME) return `Up to ${MAX_FOLDER_NAME} characters.`
  if (/[/\\]/.test(trimmed)) return 'A name cannot contain / or \\.'
  return undefined
}

export interface FolderDialogsProps {
  client: ProDocClient
  /** What the tree's menu asked for; `null` closes every dialog. */
  action: FolderAction | null
  /** The workspace's name, for "in Finance". */
  workspaceName: (id: string) => string | null
  onClose: () => void
  /** After the service said yes: the folder as it now stands, and what was done. */
  onDone: (folder: Folder, kind: FolderAction['kind']) => void
}

export function FolderDialogs({ client, action, workspaceName, onClose, onDone }: FolderDialogsProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    setBusy(false)
    setError('')
  }, [action])

  const run = async (fn: () => Promise<Folder>, kind: FolderAction['kind'], subject: Folder | null) => {
    setBusy(true)
    setError('')
    try {
      const folder = await fn()
      onDone(folder, kind)
    } catch (e) {
      setError(folderRefusalText(e, subject))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <NameDialog
        open={action?.kind === 'create'}
        title="New folder"
        intro={
          action?.kind === 'create'
            ? `In ${action.parent ? `“${action.parent.name}”` : `the top level of ${workspaceName(action.workspaceId) ?? 'the workspace'}`}. A folder is a record only: making, renaming or moving one never touches a file.`
            : ''
        }
        initial=""
        submitLabel="Create folder"
        busy={busy}
        error={error}
        onClose={onClose}
        onSave={(name) => {
          if (action?.kind !== 'create') return
          void run(() => client.folders.create({ workspace_id: action.workspaceId, parent_id: action.parent?.id ?? null, name }), 'create', action.parent)
        }}
      />
      <NameDialog
        open={action?.kind === 'rename'}
        title="Rename folder"
        intro="Every document filed in it stays where it is; nothing about the files changes."
        initial={action?.kind === 'rename' ? action.folder.name : ''}
        submitLabel="Rename"
        busy={busy}
        error={error}
        onClose={onClose}
        onSave={(name) => {
          if (action?.kind !== 'rename') return
          void run(() => client.folders.update(action.folder.id, { name }), 'rename', action.folder)
        }}
      />
      <MoveFolderDialog
        open={action?.kind === 'move'}
        client={client}
        folder={action?.kind === 'move' ? action.folder : null}
        workspaceName={action?.kind === 'move' ? workspaceName(action.folder.workspace_id) : null}
        busy={busy}
        error={error}
        onClose={onClose}
        onSave={(parentId) => {
          if (action?.kind !== 'move') return
          void run(() => client.folders.update(action.folder.id, { parent_id: parentId }), 'move', action.folder)
        }}
      />
      <ConfirmDialog
        open={action?.kind === 'archive'}
        onClose={onClose}
        title={action?.kind === 'archive' ? `Archive “${action.folder.name}”?` : 'Archive folder'}
        tone="danger"
        confirmLabel="Archive folder"
        cancelLabel="Keep it"
        busy={busy}
        error={error}
        onConfirm={() => {
          if (action?.kind !== 'archive') return
          void run(() => client.folders.archive(action.folder.id), 'archive', action.folder)
        }}
      >
        <p>It leaves the tree for everyone. Nothing is deleted, and nothing is archived with it.</p>
        <p>A folder that still holds documents or folders cannot be archived — move them out first.</p>
      </ConfirmDialog>
    </>
  )
}

/** A name, typed: for a new folder and for a rename. */
function NameDialog({
  open,
  title,
  intro,
  initial,
  submitLabel,
  busy,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  title: string
  intro: string
  initial: string
  submitLabel: string
  busy: boolean
  error: string
  onClose: () => void
  onSave: (name: string) => void
}) {
  const [name, setName] = useState(initial)
  const [tried, setTried] = useState(false)
  useEffect(() => {
    if (open) {
      setName(initial)
      setTried(false)
    }
  }, [open, initial])
  const problem = folderNameError(name)
  return (
    <Dialog open={open} onClose={onClose} title={title} error={error}>
      <form
        noValidate
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault()
          setTried(true)
          if (!problem && name.trim() !== initial.trim()) onSave(name.trim())
        }}
      >
        <p className="text-muted-foreground text-sm">{intro}</p>
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} error={tried ? problem : undefined} maxLength={MAX_FOLDER_NAME + 20} autoFocus />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} disabled={name.trim() === initial.trim()}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/**
 * Where a folder may go: the top level of its workspace, or any folder of the
 * same workspace that is not itself, not below it, and not so deep that its
 * deepest folder would pass the ninth level.
 */
export function moveTargets(all: Folder[], folder: Folder): Folder[] {
  const below = subtreeOf(all, folder.id)
  const deepest = Math.max(folder.depth, ...all.filter((f) => below.has(f.id)).map((f) => f.depth))
  const height = deepest - folder.depth
  return all.filter((f) => !below.has(f.id) && f.workspace_id === folder.workspace_id && f.depth + 1 + height <= MAX_FOLDER_DEPTH)
}

const ROOT = '__root'

function MoveFolderDialog({
  open,
  client,
  folder,
  workspaceName,
  busy,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  client: ProDocClient
  folder: Folder | null
  workspaceName: string | null
  busy: boolean
  error: string
  onClose: () => void
  onSave: (parentId: string | null) => void
}) {
  const tree = useFolderList(client, open && folder ? folder.workspace_id : null)
  const targets = useMemo(() => (folder ? moveTargets(tree.ordered, folder) : []), [tree.ordered, folder])
  const current = folder?.parent_id ?? ROOT
  const [value, setValue] = useState(current)
  useEffect(() => {
    if (open) setValue(current)
  }, [open, current])
  const hidden = folder ? tree.ordered.length - targets.length - 1 : 0

  return (
    <Dialog open={open} onClose={onClose} title={folder ? `Move “${folder.name}”` : 'Move folder'} error={error}>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault()
          onSave(value === ROOT ? null : value)
        }}
      >
        <p className="text-muted-foreground text-sm">
          Its folders and documents go with it. Only the folders’ places change — no document is rewritten and no file is touched.
        </p>
        {tree.error && !tree.data ? (
          <Alert tone="error">
            Could not load the folders: {tree.error}{' '}
            <Button variant="link" onClick={() => void tree.reload()}>
              Try again
            </Button>
          </Alert>
        ) : (
          <Select
            label="Move into"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={tree.loading && !tree.data}
            hint={hidden > 0 ? 'It cannot go inside itself, and a place that would nest it more than nine levels deep is not offered.' : 'Within its workspace only.'}
          >
            <option value={ROOT}>Top level of {workspaceName ?? 'the workspace'}</option>
            {tree.loading && !tree.data && <option disabled>Loading folders…</option>}
            {targets.map((f) => (
              <option key={f.id} value={f.id}>
                {'  '.repeat(Math.max(0, f.depth - 1))}
                {f.name}
              </option>
            ))}
          </Select>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} disabled={value === current}>
            Move
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/**
 * A folder of one workspace, chosen from its whole tree — for filing: the bulk
 * bar's *Move to folder*, the detail's move, the uploader's target. A sibling
 * app's folders are offered too (people file into them, §9-19), marked with
 * their app.
 */
export function FolderPicker({
  client,
  workspaceId,
  value,
  onChange,
  label = 'Folder',
  rootLabel = 'The workspace’s top level',
  hint,
  size,
  disabled,
  app = 'prodoc',
}: {
  client: ProDocClient
  workspaceId: string | null
  /** A folder id, or `null` for the workspace's top level. */
  value: string | null
  onChange: (folderId: string | null) => void
  label?: string
  rootLabel?: string
  hint?: string
  size?: 'sm' | 'md'
  disabled?: boolean
  /** The service's own slug: any other `owner_app` is marked as that app's folder. */
  app?: string
}) {
  const tree = useFolderList(client, workspaceId)
  return (
    <Select
      label={size === 'sm' ? undefined : label}
      aria-label={size === 'sm' ? label : undefined}
      size={size}
      value={value ?? ROOT}
      onChange={(e) => onChange(e.target.value === ROOT ? null : e.target.value)}
      disabled={disabled || !workspaceId || (tree.loading && !tree.data)}
      hint={hint}
      error={tree.error && !tree.data ? `Could not load the folders: ${tree.error}` : undefined}
    >
      <option value={ROOT}>{rootLabel}</option>
      {tree.loading && !tree.data && <option disabled>Loading folders…</option>}
      {/* A value the list does not hold yet (still loading, or out of sight) keeps its place. */}
      {value && !tree.ordered.some((f) => f.id === value) && <option value={value}>The chosen folder</option>}
      {tree.ordered.map((f) => (
        <option key={f.id} value={f.id}>
          {'  '.repeat(Math.max(0, f.depth - 1))}
          {f.name}
          {f.owner_app !== app ? ` · ${f.owner_app}` : ''}
        </option>
      ))}
    </Select>
  )
}
